/* eslint-disable no-template-curly-in-string */

import * as yup from 'yup'
import { isValidRate, MAX_RATE } from '../components/form-components/RateInlineEdit'
import { LOAD_STATE } from '../constants/loads'
import { IShipment } from '../types/CreateLoadSchema'
import { MotorCarrierSchema } from './MotorCarrierSchema'
import { ShipmentDriverSchema } from './ShipmentDriverSchema'
import { ShipmentStatusSchema } from './ShipmentStatusSchema'
import { ShipmentStopSchema } from './ShipmentStopSchema'
import { TemperatureSettingsSchema } from './TemperatureSettingsSchema'
import { isAssociatedDriver, isAssociatedDispatcher } from './utils'

const requiredMessage = 'This field is required.'

/**
 * Schema for creating a new load
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/post_shipments
 */
export const CreateLoadSchema = yup.object<IShipment>({
  customer: yup.object({ id: yup.string() }).notRequired(),
  customerContact: yup.object({ id: yup.number() }).notRequired(),
  customerNotes: yup.string().nullable().default(null),
  dispatcherId: yup
    .mixed()
    .required(requiredMessage)
    .test(
      'is-associated-dispatcher',
      'This dispatcher doesn’t have permission to manage loads for this carrier. To give them permission, you need to be an Admin for this carrier.',
      isAssociatedDispatcher
    ),
  drivers: yup
    .array()
    .of(ShipmentDriverSchema)
    .test(
      'is-associated-driver',
      'unused error string', // we use a custom one later in this function
      /**
       * Only enable this validation when `fpCarrierPortal` is enabled
       */
      isAssociatedDriver
    )
    .ensure(),
  externalId: yup.string().required(requiredMessage),
  isSample: yup.boolean().default(false),
  miles: yup.number().notRequired(),
  motorCarrierId: yup.number().required(requiredMessage),
  motorCarrier: MotorCarrierSchema.required(requiredMessage),
  notes: yup.string().notRequired().default(''),
  rate: yup
    .string()
    .notRequired()
    .test('rates-validation', `Rates must be under $${MAX_RATE}`, value => isValidRate(value)),
  status: ShipmentStatusSchema.required(requiredMessage).default(LOAD_STATE.Pending),
  stops: yup.array().of(ShipmentStopSchema).ensure(),
  temperatureSetting: TemperatureSettingsSchema.notRequired(),
  trailerId: yup.string().nullable().notRequired().default(null),
  trailerLength: yup.string().nullable().notRequired().default(null),
  trailerType: yup.string().nullable().notRequired().default(null),
  truckId: yup.string().notRequired().nullable().default(null),
})

export type CreateLoadSchemaType = yup.InferType<typeof CreateLoadSchema>
